import React, { useMemo, useState } from 'react';
import { Box, Button, Typography, Checkbox, Chip, Tooltip } from '@mui/material';
import { Lock as LockIcon, Cloud as CloudIcon } from '@mui/icons-material';
import FacetedFilterRail, {
  applyFacetFilters, emptyFacetState, type FacetConfig, type FacetFilterState,
} from '../../../shared/components/FacetedFilterRail';
import { TOOL_CATALOG, DOMAIN_LABELS, filterForAgentType } from '../../toolset/catalog';
import type { ToolCatalogEntry, ToolLevel } from '../../toolset/types';

// Bulk actions sit beside counts and headings, so they read as links rather than buttons.
const _bulk = { textTransform: 'none', minWidth: 0, px: 0.75, fontSize: 11 } as const;

const LEVEL_COLOR: Record<ToolLevel, 'default' | 'warning' | 'error'> = {
  read: 'default', write: 'warning', admin: 'error',
};

interface ToolScopePickerProps {
  agentType: 'investigation' | 'automation' | 'both';
  /** The only action_ids this agent can be given; omitted means the whole catalog. */
  selectableActionIds?: string[] | null;
  selectedActionIds: string[];
  /** The server's reason the wall will refuse a tool where the agent runs, by action_id (ADR-0071). */
  toolWarnings?: Record<string, string>;
  onToggle: (actionId: string) => void;
  /** Select or clear a set at once: everything the filter shows, or one domain of it. */
  onToggleMany: (actionIds: string[], select: boolean) => void;
}

const matchSearch = (entry: ToolCatalogEntry, query: string): boolean => {
  const q = query.toLowerCase();
  return entry.name.toLowerCase().includes(q)
    || entry.actionId.toLowerCase().includes(q)
    || entry.description.toLowerCase().includes(q);
};

/**
 * The studio's OWN tool picker — names only, description on hover. Consumes the shared
 * tool catalog (generated from the ActionRegistry) but renders compactly for prompt
 * authoring; tool management keeps its own fuller list. We own this view; we never edit
 * `toolset/` components.
 */
const ToolScopePicker: React.FC<ToolScopePickerProps> = ({
  agentType, selectableActionIds, selectedActionIds, toolWarnings, onToggle, onToggleMany,
}) => {
  const [filterState, setFilterState] = useState<FacetFilterState>(emptyFacetState);

  const catalog = useMemo(() => {
    const forAgent = filterForAgentType(TOOL_CATALOG, agentType);
    if (!selectableActionIds) return forAgent;
    const allowed = new Set(selectableActionIds);
    return forAgent.filter((entry) => allowed.has(entry.actionId));
  }, [agentType, selectableActionIds]);

  const facets: FacetConfig<ToolCatalogEntry>[] = useMemo(() => [
    { key: 'domain', title: 'Domain', keyOf: (e) => e.domain, labelOf: (k) => DOMAIN_LABELS[k] ?? k },
    { key: 'level', title: 'Access level', keyOf: (e) => e.level, order: ['read', 'write', 'admin'], advanced: true },
  ], []);

  const grouped = useMemo(() => {
    const filtered = applyFacetFilters(catalog, facets, filterState, matchSearch);
    const map = new Map<string, ToolCatalogEntry[]>();
    for (const e of filtered) {
      if (!map.has(e.domain)) map.set(e.domain, []);
      map.get(e.domain)!.push(e);
    }
    return Array.from(map.entries());
  }, [catalog, facets, filterState]);

  // A locked tool renders checked whether or not the loadout carries it, so a bulk action
  // that touched one would leave the row ticked and the count disagreeing with the draft.
  const shown = useMemo(
    () => grouped.flatMap(([, entries]) => entries.filter((e) => !e.protected).map((e) => e.actionId)),
    [grouped],
  );
  const shownSelected = shown.filter((id) => selectedActionIds.includes(id)).length;

  // A stored loadout may hold a tool no longer offered to this agent. It stays listed while the row
  // names it, so the author sees it and can remove it; the backend refuses a tools change that keeps it.
  // Only once the offered list has loaded: until then every ticked tool would read as not offered.
  const offeredIds = new Set(catalog.map((e) => e.actionId));
  const notOffered = selectableActionIds
    ? selectedActionIds.filter((id) => !offeredIds.has(id)).sort()
    : [];

  return (
    <Box sx={{ display: 'flex', flex: 1, width: '100%', minHeight: 0 }}>
      {/* Ticking a tool selects it; ticking here only narrows the list. Both are checkboxes,
          so the column says which it is and a rule keeps the two apart. */}
      <Box sx={{ width: 200, flexShrink: 0, borderRight: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
        <Typography
          variant="caption"
          sx={{ display: 'block', px: 1, pt: 1, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.4, fontSize: 10.5 }}
        >
          Narrow the list
        </Typography>
        <FacetedFilterRail
          entities={catalog}
          facets={facets}
          state={filterState}
          setState={setFilterState}
          matchSearch={matchSearch}
          searchPlaceholder="Search tools…"
        />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {shownSelected} of {shown.length} shown selected
          </Typography>
          <Button size="small" sx={_bulk} disabled={shownSelected === shown.length} onClick={() => onToggleMany(shown, true)}>
            Select all shown
          </Button>
          <Button size="small" sx={_bulk} disabled={shownSelected === 0} onClick={() => onToggleMany(shown, false)}>
            Clear shown
          </Button>
        </Box>

        <Box sx={{ flex: 1, maxHeight: 520, overflow: 'auto', p: 1 }}>
        {notOffered.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ display: 'block', px: 1, fontWeight: 700, color: 'warning.main' }}>
              No longer offered to this agent: remove these to save a change to its tools
            </Typography>
            {notOffered.map((actionId) => (
              <Box key={actionId} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25 }}>
                <Checkbox
                  size="small"
                  checked
                  onChange={() => onToggle(actionId)}
                  inputProps={{ 'aria-label': actionId }}
                  sx={{ p: 0.5 }}
                />
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5, flex: 1, minWidth: 0 }} noWrap>
                  {actionId}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        {grouped.map(([domain, entries]) => {
          const ids = entries.filter((e) => !e.protected).map((e) => e.actionId);
          const selectedHere = ids.filter((id) => selectedActionIds.includes(id)).length;
          return (
          <Box key={domain} sx={{ mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.5, flex: 1 }}
              >
                {DOMAIN_LABELS[domain] ?? domain}
              </Typography>
              <Button
                size="small"
                sx={_bulk}
                disabled={selectedHere === ids.length}
                aria-label={`Select every ${DOMAIN_LABELS[domain] ?? domain} tool`}
                onClick={() => onToggleMany(ids, true)}
              >
                all
              </Button>
              <Button
                size="small"
                sx={_bulk}
                disabled={selectedHere === 0}
                aria-label={`Clear every ${DOMAIN_LABELS[domain] ?? domain} tool`}
                onClick={() => onToggleMany(ids, false)}
              >
                none
              </Button>
            </Box>
            {entries.map((entry) => {
              const locked = Boolean(entry.protected);
              const checked = locked || selectedActionIds.includes(entry.actionId);
              return (
                <Box
                  key={entry.actionId}
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1, py: 0.25, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={locked}
                    onChange={() => onToggle(entry.actionId)}
                    inputProps={{ 'aria-label': entry.actionId }}
                    sx={{ p: 0.5 }}
                  />
                  {entry.aws && <CloudIcon sx={{ fontSize: 14, color: 'info.main' }} />}
                  <Tooltip
                    placement="top-start"
                    title={<><strong>{entry.actionId}</strong><br />{entry.description}</>}
                  >
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5, flex: 1, minWidth: 0 }} noWrap>
                      {entry.name}
                    </Typography>
                  </Tooltip>
                  {toolWarnings?.[entry.actionId] && (
                    <Typography variant="caption" color="warning.main" sx={{ flexShrink: 0 }}>
                      {toolWarnings[entry.actionId]}
                    </Typography>
                  )}
                  {locked && (
                    <Tooltip title="Always on — a runtime hook depends on this tool">
                      <LockIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                    </Tooltip>
                  )}
                  <Chip
                    label={entry.level}
                    size="small"
                    variant="outlined"
                    color={LEVEL_COLOR[entry.level]}
                    sx={{ height: 18, fontSize: 10, textTransform: 'uppercase', flexShrink: 0 }}
                  />
                </Box>
              );
            })}
          </Box>
          );
        })}
        {grouped.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No tools match the filter</Typography>
          </Box>
        )}
        </Box>
      </Box>
    </Box>
  );
};

export default ToolScopePicker;
